import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons"
import { SortValue, SortValues } from "interface/sort"
import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router"

export function useListSort<T extends Record<string, any>>(
	defaultSort: SortValues<T> = [],
) {
	const defaultSortKey = defaultSort.join(",")
	const defaultInit = useMemo(
		() => (defaultSort.length > 0 ? { sort: [...defaultSort] } : undefined),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[defaultSortKey],
	)
	const [searchParams, setSearchParams] = useSearchParams(defaultInit)
	const sort = searchParams.getAll("sort") as SortValues<T>
	const sortIcon = useCallback(
		(sortablekey: keyof T) =>
			sort.includes(`${String(sortablekey)}-ascending`) ? (
				<ArrowDownOutlined aria-hidden />
			) : (
				<ArrowUpOutlined aria-hidden />
			),
		[sort],
	)
	const toggle = useCallback(
		(sortablekey: keyof T) => {
			const ascending = `${String(sortablekey)}-ascending` as SortValue<T>
			const descending = `${String(sortablekey)}-descending` as SortValue<T>
			const newSort = sort.map((item) => {
				if (item.includes(String(sortablekey))) {
					if (item === ascending) {
						return descending
					} else {
						return ascending
					}
				} else {
					return item
				}
			})
			// Ensure that the sortable key is at the start of the array
			// Since the first item in the array has the highest priority
			const sortItem = newSort.find((item) =>
				item.includes(String(sortablekey)),
			)
			if (sortItem) {
				newSort.splice(newSort.indexOf(sortItem), 1)
				newSort.unshift(sortItem)
			}
			setSearchParams({
				...Object.fromEntries(searchParams.entries()),
				sort: newSort,
			})
		},
		[searchParams, setSearchParams, sort],
	)
	const labelSort = useCallback(
		(sortablekey: keyof T, fieldName: string) => {
			const direction = sort
				.find((s) => s.includes(sortablekey as string))
				?.split("-")[1]
			return `Sort ${fieldName} ${direction}`
		},
		[sort],
	)

	return {
		sort,
		toggle,
		sortIcon,
		labelSort,
	}
}
